import { Button, CircularProgress, type ButtonProps } from '@mui/material';

interface SubmitButtonProps extends Omit<ButtonProps, 'disabled'> {
  /** True while the request is in flight — disables the button and shows the spinner + busy label. */
  loading?: boolean;
  /** Idle label, e.g. "Create". */
  children: React.ReactNode;
  /** Busy label, e.g. "Creating…". Defaults to "Saving…". */
  loadingText?: string;
  /** Disable independently of loading (e.g. nothing changed). */
  disabled?: boolean;
}

/**
 * Save/submit button with built-in busy UX (Phase 10): while {@link loading} it is disabled, shows a
 * spinner, and swaps to a present-tense busy label ("Saving…", "Creating…", "Sending…"). Use across
 * every form so the in-flight state is consistent and double-submits are prevented.
 */
export default function SubmitButton({
  loading = false,
  loadingText = 'Saving…',
  disabled = false,
  children,
  variant = 'contained',
  type = 'submit',
  ...rest
}: SubmitButtonProps) {
  return (
    <Button
      {...rest}
      type={type}
      variant={variant}
      disabled={loading || disabled}
      startIcon={loading ? <CircularProgress size={16} color="inherit" /> : rest.startIcon}
    >
      {loading ? loadingText : children}
    </Button>
  );
}
